import _ from "lodash";
import React, { useState } from "react";

import { CATCH_ALL_INSURANCE_OPTIONS } from "@/core/constants";
import { getCarrierMetadata } from "@/components/Common/InsuranceEligibility/carrierMetadata";
import { insuranceListToSelectFormat } from "@/utils/GenericListUtils";

import { AddInsurance } from "../../AddInsurance";

export function ProviderReferralHOC({
  firstName,
  formikProps,
  insuranceCompanyOptions,
  isPrimaryInsurance,
  lastName,
  onBrowseButtonClicked,
  ...props
}) {
  const { errors, handleBlur, handleChange, handleFocus, touched, values } = formikProps;
  const { groupIdTooltipContents, memberIdPlaceholder, memberIdTip, memberIdTooltipContents } =
    getCarrierMetadata(values.insurance[isPrimaryInsurance ? 0 : 1].form.carrier);
  const [noInsurance, setNoInsurance] = useState(false);

  const mapDropDownValue = (value) => ({
    label: value,
    value: value,
  });

  const getPolicyHolderValue = () => {
    if (values.insurance[isPrimaryInsurance ? 0 : 1].policy_holder_question) {
      return values.insurance[isPrimaryInsurance ? 0 : 1].policy_holder_question;
    }

    return "yes";
  };

  const mappedValues = {
    groupId: values.insurance[isPrimaryInsurance ? 0 : 1].form.group_id,
    insuranceCompany: mapDropDownValue(values.insurance[isPrimaryInsurance ? 0 : 1].form.carrier),
    memberId: values.insurance[isPrimaryInsurance ? 0 : 1].form.member_id,
    policyHolder: getPolicyHolderValue(),
    policyHolderName: `${firstName} ${lastName}`,
  };

  const mappedNames = {
    groupId: `insurance[${isPrimaryInsurance ? "0" : "1"}].form.group_id`,
    insuranceBack: `insurance[${isPrimaryInsurance ? "0" : "1"}].form.card_back`,
    insuranceCompany: `insurance[${isPrimaryInsurance ? "0" : "1"}].form.carrier`,
    insuranceFront: `insurance[${isPrimaryInsurance ? "0" : "1"}].form.card_front`,
    memberId: `insurance[${isPrimaryInsurance ? "0" : "1"}].form.member_id`,
    policyHolder: `insurance[${isPrimaryInsurance ? "0" : "1"}].form.policy_holder_question`,
    policyHolderName: `insurance[${isPrimaryInsurance ? "0" : "1"}].form.policy_holder_name`,
    policyRelationship: `insurance[${isPrimaryInsurance ? "0" : "1"}].form.policy_relationship`,
  };

  const mappedTouched = {
    groupId: _.get(touched, `insurance[${isPrimaryInsurance ? "0" : "1"}].form.group_id`),
    insuranceCompany: _.get(touched, `insurance[${isPrimaryInsurance ? "0" : "1"}].form.carrier`),
    memberId: _.get(touched, `insurance[${isPrimaryInsurance ? "0" : "1"}].form.member_id`),
    policyHolder: _.get(
      touched,
      `insurance[${isPrimaryInsurance ? "0" : "1"}].form.policy_holder_question`,
    ),
  };

  const mappedErrors = {
    groupId: _.get(errors, `insurance[${isPrimaryInsurance ? "0" : "1"}].form.group_id`),
    insuranceCompany: _.get(errors, `insurance[${isPrimaryInsurance ? "0" : "1"}].form.carrier`),
    memberId: _.get(errors, `insurance[${isPrimaryInsurance ? "0" : "1"}].form.member_id`),
    policyHolder: _.get(
      errors,
      `insurance[${isPrimaryInsurance ? "0" : "1"}].form.policy_holder_question`,
    ),
  };

  const generatePolicyHolderName = (e, value) => {
    let policyHolderName = "Other";

    if (value === "yes") {
      policyHolderName = `${firstName} ${lastName}`;
    }
    handleChange({
      ...e,
      target: { ...e.target, name: mappedNames["policyHolderName"], value: policyHolderName },
    });
  };

  const generatePolicyHolderRelationship = (e, value) => {
    let policyHolderRelationship = "Other";

    if (value === "yes") {
      policyHolderRelationship = "self";
    }
    handleChange({
      ...e,
      target: {
        ...e.target,
        name: mappedNames["policyRelationship"],
        value: policyHolderRelationship,
      },
    });
  };

  const mappedChangeHandler = (e) => {
    const { name, value } = e.target;
    const mappedName = mappedNames[name];
    let mappedValue = value;

    switch (name) {
      case "insuranceCompany":
        if (CATCH_ALL_INSURANCE_OPTIONS.includes(value)) {
          resetInsurance();
          setNoInsurance(true);
        } else {
          setNoInsurance(false);
        }
        break;

      case "policyHolder":
        generatePolicyHolderName(e, value);
        generatePolicyHolderRelationship(e, value);
        break;
    }

    handleChange({ ...e, target: { ...e.target, name: mappedName, value: mappedValue } });
  };

  const resetInsurance = () => {
    for (const name in mappedNames) {
      if (name === "insuranceCompany") {
        continue;
      }
      formikProps.setFieldValue(mappedNames[name], "");
    }
  };

  const onFileDeleteHandler = (fileName) => {
    let insuranceSide = "insuranceBack";

    if (fileName.startsWith("primary_front")) {
      insuranceSide = "insuranceFront";
    }

    handleChange({ target: { name: mappedNames[insuranceSide], value: null } });
  };

  const getInsuranceFileNames = () => {
    const fileNameArray = [];
    const frontImage = _.get(values, mappedNames["insuranceFront"]);
    const backImage = _.get(values, mappedNames["insuranceBack"]);

    if (frontImage) {
      fileNameArray.push(frontImage);
    }
    if (backImage) {
      fileNameArray.push(backImage);
    }

    return fileNameArray;
  };

  return (
    <AddInsurance
      errors={mappedErrors}
      files={getInsuranceFileNames()}
      groupIdTooltipContents={groupIdTooltipContents}
      handleBlur={handleBlur}
      handleChange={mappedChangeHandler}
      handleFocus={handleFocus}
      insuranceCompanyOptions={insuranceListToSelectFormat(insuranceCompanyOptions)}
      isPrimaryInsurance={isPrimaryInsurance}
      memberIdPlaceholder={memberIdPlaceholder}
      memberIdTip={memberIdTip}
      memberIdTooltipContents={memberIdTooltipContents}
      noInsurance={noInsurance}
      onBrowseButtonClicked={onBrowseButtonClicked}
      onFileDelete={onFileDeleteHandler}
      policyHolderLabel="Is the Patient the Policy Holder?"
      touched={mappedTouched}
      values={mappedValues}
      {...props}
    />
  );
}
